import qs from "qs";

import {
  AuthenticateMethod,
  type AuthenticateResultErrorType,
  type DataTypeStatus,
  type webAgent__EndpointType,
} from "@medbillai/generated-graphql-types";

import type { CaseType } from "@medbillai/schemas";

export type LoginParams = {
  forceLogout?: boolean;
  authError?: AuthenticateResultErrorType;
  redirect?: string;
  /**
   * Selects the email-entry screen's framing. The intent values are set by
   * the onboarding v2 handoff (title + subtitle copy); `signup` is the v3
   * signup handoff (create-account framing + web hero). Riding the URL keeps
   * the framing refresh-proof. Not preserved through the magic-link round
   * trip.
   */
  variant?: "bill" | "learn" | "other" | "signup";
};

export type PreviewParams = {
  title: string;
  uri: string;
};

export type HipaaReleaseParams = {
  templateId?: string;
  eventId?: string;
  issueId?: string;
};

export type HipaaViewReleaseParams = {
  templateReleaseId: string;
  templateDisplayName?: string;
};

export type HipaaErrorParams = {
  viewPdfError?: boolean;
};

export interface CreateAuthRedirectUriOptions {
  baseUri: string;
  platform: "ios" | "android" | "web";
  method: AuthenticateMethod;
  applinkSchemeUri?: string;
}

export type FeedbackParams = {
  issueId: string;
  rating?: number;
  feedbackId?: string;
};

export type FeedbackDeepLinkParams = FeedbackParams & {
  showFeedback?: boolean;
};

export type AuthenticateRedirectUrlParams = {
  token: string;
  platform: string;
};

export type StytchAuthenticationParams = AuthenticateRedirectUrlParams & {
  stytch_token_type: string;
};

export type PreviewCaseMessageInfo =
  | {
      type: "USER";
      message: string;
    }
  | {
      type: "SYSTEM";
      messageId: string;
      message: string;
      options?: string[];
    };

export const CASE_PREVIEW_ID = "preview";

/**
 * Appends query parameters to a URL, handling both URLs with and without existing query strings.
 * If the URL already contains query parameters (has a '?'), new params are appended with '&'.
 * Otherwise, new params are appended with '?'.
 */
export const buildUrlWithParams = (
  url: string,
  params?: Record<string, unknown>,
): string => {
  const filteredParams = Object.fromEntries(
    Object.entries(params || {}).filter(([_, value]) => value !== undefined),
  );
  if (Object.keys(filteredParams).length > 0) {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${qs.stringify(filteredParams)}`;
  }
  return url;
};

// TODO - look into generating these

// Onboarding & auth
export const welcomeUrl = (): string => "/welcome";
export const getStartedUrl = (): string => "/get-started";
export const onboardingResumeUrl = (): string => "/onboarding/resume";
export const activateUrl = (params?: { inviteToken?: string }): string =>
  buildUrlWithParams("/activate", params);
// Path says "partner", not the partner's name: FFGA (or any partner) must
// never appear in user-visible surfaces.
export const onboardingFfgaResumeUrl = (): string =>
  "/onboarding/partner-resume";
export const onboardingV3ResumeUrl = (): string => "/onboarding/v3-resume";
export const internalOnboardingFlowV3Url = (
  slug: string,
  options?: { intent?: string; skipSignUp?: boolean },
): string => {
  const params = new URLSearchParams();
  if (options?.intent) {
    params.set("intent", options.intent);
  }
  if (options?.skipSignUp) {
    params.set("skipSignUp", "true");
  }
  const query = params.toString();
  return `/internal/onboarding-flow-v3/${slug}${query ? `?${query}` : ""}`;
};
export const loginUrl = (params?: LoginParams): string => {
  if (!params) {
    return "/login";
  }
  return `/login?${qs.stringify(params)}`;
};
export const verifyEmailUrl = (email: string): string => {
  if (!email) {
    return "/login";
  }
  return `/verify-email?email=${encodeURIComponent(email)}`;
};
// Used for magic links with the paste url button.
export const authenticateUrl = (params: StytchAuthenticationParams): string =>
  buildUrlWithParams("/authenticate", params);

export const createAuthRedirectUri = ({
  baseUri,
  applinkSchemeUri,
  platform,
  method,
}: CreateAuthRedirectUriOptions): string => {
  // In this case we want to ensure the redirect is always navigated back into
  // the app. Google oauth if already authenticated will stay in the iframe, so
  // we use the applinkSchemeUri to navigate back into the app.
  // If the applinkSchemeUri is not set, we default to the baseUri.
  if (
    method === AuthenticateMethod.OAUTH &&
    (platform === "ios" || platform === "android") &&
    applinkSchemeUri
  ) {
    return `${applinkSchemeUri}authenticate?platform=${platform}`;
  }
  const url = new URL("/authenticate", baseUri);
  url.searchParams.set("platform", platform);
  return url.toString();
};

// Pre-auth (anonymous bill upload flow)
export const preAuthBillUploadUrl = (): string => "/pre-auth/bill-upload";
export const preAuthCaseUrl = (
  issueId: string,
  params?: { userId?: string },
): string => buildUrlWithParams(`/pre-auth/case/${issueId}`, params);

// Home Tabs
export const homeUrl = (): string => "/";
// Should only be used when navigating inside the tab router
// TODO: Because of the limitation of using headerLarge needing a stack router
// we cannot have a default initial route for the tab router, we need to update
// all routing to use /home but in the mean time because it is the default route
// we can use this till we update all routing, this will effect the web app
// minorly but it is a small fix
export const homeTabsUrl = (): string => "/home";
export const accountsUrl = (): string => "/accounts";
export const issuesUrl = (): string => `/cases`;
export const billsUrl = (): string => `/bills`;
export const flaggedBillsUrl = (): string => `/bills/flagged`;

// Flow
export const documentUrl = (documentId: string): string =>
  `/document/${documentId}`;
export const documentPreviewUrl = () => "/document/preview";

export const issueUrl = ({
  issueId,
  unread = false,
  skipPageUILoad,
  title,
}: {
  issueId: string;
  unread?: boolean;
  skipPageUILoad?: boolean;
  title?: string | null;
}): string => {
  const baseUrl = `/case/${issueId}`;
  const params = {
    unread: unread.toString(),
    ...(skipPageUILoad && { skipPageUILoad: skipPageUILoad.toString() }),
    ...(title && { title }),
  };
  return buildUrlWithParams(baseUrl, params);
};
export const issuePreviewUrl = (params: {
  title?: string;
  caseType?: CaseType;
  storeKey?: string;
  config?: string;
}) => {
  return buildUrlWithParams(`/case/${CASE_PREVIEW_ID}`, params);
};

// Bills
export const billUrl = (
  eobId: string,
  billName: string,
  decision: string,
  kind?: "claim" | "invoice",
): string => {
  const base = `/bill/${eobId}?billName=${billName}&decision=${decision}`;
  return kind === "invoice" ? `${base}&kind=invoice` : base;
};
export const billDisputeUrl = (eobId: string): string => {
  return `/bill-dispute?id=${eobId}`;
};

// Configuration / Onboarding
export const linkInsuranceUrl = (): string => "/link-insurance";
export const allowNotificationsUrl = (): string => "/allow-notifications";
export const onboardingCompletedUrl = (): string => "/completed";

// Hipaa Release
export const hipaaReleaseUrl = (params?: HipaaReleaseParams): string =>
  `/hipaa-release?${qs.stringify(params)}`;
export const hipaaViewReleaseUrl = (params?: HipaaViewReleaseParams): string =>
  `/hipaa-release-preview?${qs.stringify(params)}`;
export const hipaaReleaseErrorUrl = (params?: HipaaErrorParams): string =>
  `/hipaa-release/error?${qs.stringify(params)}`;
export const hipaaReleasePatientSelect = (): string =>
  "/hipaa-release/patient-select";
export const hipaaReleasePatientInformation = (): string =>
  "/hipaa-release/patient-information";
export const hipaaReleasePatientAddress = (): string =>
  "/hipaa-release/patient-address";
export const hipaaReleasePatientContact = (): string =>
  "/hipaa-release/patient-contact";
export const hipaaReleasePatientInsurance = (): string =>
  "/hipaa-release/patient-insurance";
export const hipaaReleaseDependentCheck = (): string =>
  "/hipaa-release/dependent-check";
export const hipaaReleaseDependentInformation = (): string =>
  "/hipaa-release/dependent-information";
export const hipaaReleaseSubscriberCheck = (): string =>
  "/hipaa-release/subscriber-check";
export const hipaaReleaseSubscriberInformation = (): string =>
  "/hipaa-release/subscriber-information";
export const hipaaReleasePatientAdditional = (): string =>
  "/hipaa-release/patient-additional";
export const hipaaReleaseSubscriberSelect = (): string =>
  "/hipaa-release/subscriber-select";
export const hipaaReleaseReview = (): string => "/hipaa-release/review";
export const hipaaReleaseSign = (): string => "/hipaa-release/sign";
export const hipaaReleaseSuccess = (): string => "/hipaa-release/success";
export const hipaaReleaseSubscriberInsurance = (): string =>
  "/hipaa-release/subscriber-insurance";
export const hipaaReleaseSubscriberAdditional = (): string =>
  "/hipaa-release/subscriber-additional";

// Case Intake

// This should most likely never be used directly.
// When navigating to a case intake flow, use the useSetupCaseFlow hook
export const caseIntakeUrl = (): string => {
  return `/case-intake`;
};

export const caseIntakeFlowUrl = (step: string): string => {
  return `${caseIntakeUrl()}/${step}`;
};
export const caseIntakeFlowErrorUrl = (): string => "/case-intake/error";
export const caseDetailsUrl = (caseId: string): string => {
  return `/case/details/${caseId}`;
};
// Settings
export const settingsUrl = (): string => "/settings";
export const settingPreviewUrl = (params: PreviewParams): string => {
  return `/settings/preview?${qs.stringify(params)}`;
};
export const settingsReleaseListUrl = (): string => "/settings/release-list";
export const settingsAccountSettingsUrl = (): string => "/settings/account";
export const settingsDeleteAccountUrl = (): string =>
  "/settings/delete-account";

export const settingsReceiptsUrl = (): string => "/settings/receipts";

// Insurance
// Accounts Link
export type InsuranceLinkType = webAgent__EndpointType | "all";
export type InsuranceLinkFrom =
  | {
      placeFrom: "insuranceHome";
      linkType?: InsuranceLinkType;
    }
  | {
      placeFrom: "chat";
      chatId: string;
      linkType?: InsuranceLinkType;
    };

// Accounts Link - Base
export const accountsLinkUrl = (): string => "/accounts-link";
export const accountsLinkSelectUrl = (from?: InsuranceLinkFrom): string =>
  buildUrlWithParams("/accounts-link/select", from);
export const accountsLinkErrorUrl = (): string => "/accounts-link/error";
export const accountsLinkUnsupportedPlanUrl = (): string =>
  "/accounts-link/unsupported-plan";

export const accountsLinkNotificationsUrl = (): string =>
  "/accounts-link/accounts-notifications";

// Accounts Link - Web Agent
export const accountsLinkWebAgentLinkUrl = (): string => "/accounts-link/link";

export type AccountsLinkWebAgentRelinkUrlParams = Record<string, never>;
export const accountsLinkWebAgentRelinkUrl = ({
  linkId,
  params,
}: {
  linkId: string;
  params?: AccountsLinkWebAgentRelinkUrlParams;
}): string => {
  return buildUrlWithParams(`/accounts-link/link/relink/${linkId}`, params);
};

export const accountsLinkWebAgentMfaSelectUrl = (): string =>
  "/accounts-link/link/mfa-select";
export const accountsLinkWebAgentMfaCodeUrl = (): string =>
  "/accounts-link/link/mfa-code";
export const accountsLinkWebAgentSecurityQuestionUrl = (): string =>
  "/accounts-link/link/security-question";
export const accountsLinkWebAgentSuccessUrl = (params: {
  hasBenefitsSummary: boolean;
}): string => {
  const baseUrl = "/accounts-link/link/success";
  return `${baseUrl}?hasBenefitsSummary=${params.hasBenefitsSummary.toString()}`;
};
export const accountsLinkWebAgentNotificationsUrl = (): string =>
  "/accounts-link/link/accounts-notifications";
export const accountsLinkWebAgentAlternateInsuranceUrl = (): string =>
  "/accounts-link/link/alternate-account";
export const accountsLinkWebAgentPlanDocumentsLoadingUrl = (): string =>
  "/accounts-link/link/plan-documents-loading";
export function accountsLinkWebAgentPlanDocumentsLoadedUrl(
  status: DataTypeStatus.SUCCESS,
  caseId: string,
): string;
export function accountsLinkWebAgentPlanDocumentsLoadedUrl(
  status: Exclude<DataTypeStatus, DataTypeStatus.SUCCESS>,
): string;
export function accountsLinkWebAgentPlanDocumentsLoadedUrl(
  status: DataTypeStatus,
  caseId?: string,
): string {
  return buildUrlWithParams("/accounts-link/link/plan-documents-loaded", {
    status,
    caseId,
  });
}

// Accounts Link - Flexpa
export const accountsLinkFlexpaLinkUrl = (): string =>
  "/accounts-link/connect/login";
export const accountsLinkFlexpaCheckPlanUrl = (): string =>
  "/accounts-link/connect/check-plan";
export const accountsLinkFlexpaSuccessUrl = (): string =>
  "/accounts-link/connect/success";
export const accountsLinkFlexpaNotificationsUrl = (): string =>
  "/accounts-link/connect/accounts-notifications";
export const accountsLinkFlexpaAlternateInsuranceUrl = (): string =>
  "/accounts-link/connect/alternate-account";

// Note: feedback used to be its own route, but this now goes directly to the
// case screen with feedback params. The old route still exists, and redirects
// to the case screen, but this function goes directly to the case screen to
// save future deeplinks the bounce.
export const feedbackDeepLinkUrl = (params: FeedbackDeepLinkParams): string => {
  const caseUrl = issueUrl({ issueId: params.issueId, unread: true });
  return buildUrlWithParams(caseUrl, {
    showFeedback: params.showFeedback ?? "true",
    rating: params.rating?.toString(),
    feedbackId: params.feedbackId,
  });
};

// Person
export const peopleUrl = (): string => "/people";
export const peopleCreateUrl = (): string => "/people/create";
export const peopleEditUrl = (personId: string): string =>
  `/people/edit?id=${personId}`;

// Payment
export const paymentPreauthorizeUrl = (
  eventId?: string,
  issueId?: string,
): string => buildUrlWithParams("/payment/preauthorize", { eventId, issueId });
export const paymentSuccessUrl = (): string => "/payment/success";
export const paymentAgreementUrl = (
  eventId: string,
  issueId?: string,
): string =>
  buildUrlWithParams("/payment/payment-agreement", { eventId, issueId });

// Admin
export const shadowLoginUrl = (): string => "/shadow-login";

// Mobile only
export const previewUrl = (params: PreviewParams): string => {
  return `/preview?${qs.stringify(params)}`;
};

/** Returns true when the path belongs to the cases list or an individual case. */
export const isCaseRoute = (path: string): boolean =>
  path === "/cases" || path.startsWith("/case/");

// Admin URLs (GrantCentral internal tools)
export const adminCaseUrl = (caseId: string): string =>
  `/ha/case?issueId=${caseId}`;

export const adminUserUrl = (userId: string): string =>
  `/ha/manage-user?userId=${userId}`;
